import { escapeHtml } from './htmlEscape.js'

/**
 * Parse commit body into structured sections delimited by ## headers.
 * Returns { summary?, work?, impact?, impactIntro?, raw? }
 * Falls back to { raw: body } for old-format commits without ## headers.
 *
 * Behavior must remain identical to the previous inline implementation:
 * the commit email relies on the exact field names returned here.
 */
export function parseCommitSections(body) {
  if (!body || !body.trim()) return null

  const trimmed = body.trim()

  // If body doesn't use ## format, return raw fallback
  if (!trimmed.includes('## ')) {
    return { raw: trimmed }
  }

  const sections = {}
  const parts = trimmed.split(/^## /m)

  for (const part of parts) {
    const t = part.trim()
    if (!t) continue

    const newlineIdx = t.indexOf('\n')
    if (newlineIdx === -1) continue

    const header = t.substring(0, newlineIdx).trim()
    const content = t.substring(newlineIdx + 1).trim()

    if (header === 'תקציר מנהלים') sections.summary = content
    else if (header === 'מה בוצע בפועל') sections.work = content
    else if (header === 'המשמעות התפעולית') {
      // Separate optional intro line from bullet points
      const lines = content.split('\n').filter((l) => l.trim())
      const firstBulletIdx = lines.findIndex((l) => /^\s*[-•]/.test(l))
      if (firstBulletIdx > 0) {
        sections.impactIntro = lines.slice(0, firstBulletIdx).join(' ').trim()
        sections.impact = lines.slice(firstBulletIdx).join('\n').trim()
      } else {
        sections.impact = content
      }
    }
  }

  if (sections.summary || sections.work || sections.impact) {
    return sections
  }

  return { raw: trimmed }
}

/**
 * Convert numbered list text into HTML with <strong> numbered prefixes.
 * Preserves the exact whitespace indentation that the commit email
 * template relies on (10 trailing spaces around the join glue).
 */
export function textToNumberedHtml(text) {
  if (!text) return ''
  return text
    .split('\n')
    .filter((l) => l.trim())
    .map((line) => {
      const match = line.match(/^\s*(\d+)\.\s+(.*)/)
      if (match) {
        return `<strong>${escapeHtml(match[1])}.</strong> ${escapeHtml(match[2])}<br />`
      }
      return `${escapeHtml(line.trim())}<br />`
    })
    .join('\n                      ')
}

/**
 * Convert bullet list text into HTML bullet points.
 * Preserves the exact whitespace indentation used by the commit email template.
 */
export function textToBulletHtml(text) {
  if (!text) return ''
  return text
    .split('\n')
    .filter((l) => l.trim())
    .map((line) => {
      const cleaned = line.replace(/^\s*[-•*]\s+/, '').trim()
      return `• ${escapeHtml(cleaned)}<br />`
    })
    .join('\n                    ')
}
